import { computed, toValue } from 'vue';

import {
  getAssetContainerStatusColor,
  getAssetContainerStatusIcon
} from '@satellite/composables/yardAssetPresenterShared';
import { useYardAssetRowsPresenter } from '@satellite/composables/useYardAssetRowsPresenter';
import {
  isAssetDisabledInSelect,
  isAssetExcludedFromSelect,
  resolveAssetSelectState
} from '@satellite/composables/yard/yardAssetSelectRules';
import { AssetContainerEventType } from '@nova/core';

import type { MaybeRefOrGetter, ComputedRef } from 'vue';
import type { YardAssetRow } from '@satellite/composables/useYardAssetRowsPresenter';
import type {
  YardAssetSelectAxes,
  YardAssetSelectAxis,
  YardAssetSelectState
} from '@satellite/composables/yard/yardAssetSelectRules';

const STATE_EVENT_TYPES: Partial<Record<YardAssetSelectState, AssetContainerEventType>> = {
  inTransit: AssetContainerEventType.InTransit,
  missing: AssetContainerEventType.Missing
};

interface IProcessedAsset {
  assetId: string;
  assetCode: string;
  icon: string;
  iconColor: string | undefined;
  isAssigned: boolean;
  isMissing: boolean;
  isInTransit: boolean;
  spotAreaName: string | null;
  spotId: string | null;
  spotCode: string | null;
  disabled: boolean;
}

/**
 * Options for useYardAssetSelect composable.
 *
 * `warehouseId` wants a getter, not a value: a Pinia store is `reactive`, so
 * `store.selectedWarehouseId` reads as a plain string and passing it directly would pin the picker
 * to whatever warehouse was selected when the dialog mounted — and these dialogs never unmount.
 */
interface IUseYardAssetSelectOptions {
  warehouseId?: MaybeRefOrGetter<string | null>;
  includeWaitingAssignmentAssets?: MaybeRefOrGetter<YardAssetSelectAxis>;
  includeAssignedAssets?: MaybeRefOrGetter<YardAssetSelectAxis>;
  includeMissingAssets?: MaybeRefOrGetter<YardAssetSelectAxis>;
  includeInTransitAssets?: MaybeRefOrGetter<YardAssetSelectAxis>;
  includeAssociatedToActiveMoveTasks?: MaybeRefOrGetter<YardAssetSelectAxis>;
  /** Asset already on the task being edited: listed and selectable whatever its state. */
  currentAssetId?: MaybeRefOrGetter<string | null | undefined>;
  search?: MaybeRefOrGetter<string>;
  showDisabled?: MaybeRefOrGetter<boolean>;
}

interface IYardAssetSelect {
  assetsRef: ComputedRef<IProcessedAsset[]>;
  isLoadingRef: ComputedRef<boolean>;
  refresh: () => Promise<void>;
}

/**
 * Composable for asset selection functionality.
 *
 * Rows come from the `yard/view/assets` BFF, which already answers everything the picker needs per
 * container: code, spot, status and the open move tasks holding it. Filtering and search stay
 * client-side because the endpoint returns the whole option set. Which states are listed, greyed
 * out or dropped is decided by the shared rules in `yardAssetSelectRules`, so Helios and this
 * picker cannot disagree.
 */
export function useYardAssetSelect(options: IUseYardAssetSelectOptions = {}): IYardAssetSelect {
  const {
    warehouseId,
    includeWaitingAssignmentAssets = false,
    includeAssignedAssets = false,
    includeMissingAssets = true,
    includeInTransitAssets = true,
    includeAssociatedToActiveMoveTasks = false,
    currentAssetId,
    search = '',
    showDisabled = true
  } = options;

  // `headless`: the dialog is mounted for the whole authenticated session (see `App.vue`), so there
  // is no viewport to gate the fetch on.
  const {
    data: assetRows,
    isLoading,
    refresh
  } = useYardAssetRowsPresenter({
    warehouseId: computed(() => toValue(warehouseId) || null),
    headless: true
  });

  const selectAxes = computed<YardAssetSelectAxes>(() => ({
    includeAssignedAssets: toValue(includeAssignedAssets),
    includeWaitingAssignmentAssets: toValue(includeWaitingAssignmentAssets),
    includeMissingAssets: toValue(includeMissingAssets),
    includeInTransitAssets: toValue(includeInTransitAssets),
    includeAssociatedToActiveMoveTasks: toValue(includeAssociatedToActiveMoveTasks)
  }));

  const isCurrentlySelectedAsset = (asset: YardAssetRow): boolean => {
    const selectedId = toValue(currentAssetId);

    return Boolean(selectedId) && asset.id === selectedId;
  };

  /**
   * Process and format assets for display in select component
   */
  const processedAssets = computed(() => {
    const searchQuery = toValue(search)?.trim().toLowerCase() || '';
    const showDisabledValue = toValue(showDisabled);

    /**
     * Helper function to check if a string value matches the search query.
     * Handles null/undefined values and performs case-insensitive matching.
     */
    const matchesSearchString = (value: string | null | undefined): boolean => {
      if (!value || !searchQuery) {
        return false;
      }
      return value.toLowerCase().includes(searchQuery);
    };

    const matchesSearch = (asset: YardAssetRow): boolean => {
      if (!searchQuery) {
        return true;
      }

      return (
        matchesSearchString(asset.code) ||
        matchesSearchString(asset.spotArea?.name) ||
        matchesSearchString(asset.spot?.code)
      );
    };

    const isDisabled = (asset: YardAssetRow): boolean =>
      !isCurrentlySelectedAsset(asset) && isAssetDisabledInSelect(asset, selectAxes.value);

    return assetRows.value
      .filter(asset => {
        if (
          !isCurrentlySelectedAsset(asset) &&
          isAssetExcludedFromSelect(asset, selectAxes.value)
        ) {
          return false;
        }

        if (!matchesSearch(asset)) {
          return false;
        }

        return showDisabledValue || !isDisabled(asset);
      })
      .map(asset => {
        const state = resolveAssetSelectState(asset);
        const eventType = STATE_EVENT_TYPES[state];

        return {
          assetId: asset.id,
          assetCode: asset.code,
          icon: eventType
            ? getAssetContainerStatusIcon(eventType)
            : state === 'assigned'
              ? 'mdi-truck-trailer'
              : 'mdi-clock-outline',
          iconColor: eventType ? getAssetContainerStatusColor(eventType) : undefined,
          isAssigned: state === 'assigned',
          isMissing: state === 'missing',
          isInTransit: state === 'inTransit',
          spotAreaName: asset.spotArea?.name ?? null,
          spotCode: asset.spot?.code ?? null,
          spotId: asset.spot?.id ?? null,
          disabled: isDisabled(asset)
        };
      })
      .sort((a, b) => a.assetCode.localeCompare(b.assetCode));
  });

  return {
    assetsRef: processedAssets,
    isLoadingRef: isLoading,
    refresh
  };
}
